import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { ToastContext } from "./toast-context";

/** How long a toast stays on screen. */
export const TOAST_MS = 2500;

/**
 * GRF-27: one toast at a time, bottom centre, gone after {@link TOAST_MS}. Hidden from assistive technology: callers
 * announce the same words through the app's one polite live region (useCopy does), so nothing is read twice.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);

  const show = useCallback((next: string) => {
    setMessage(next);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setMessage(null), TOAST_MS);
  }, []);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  return (
    <ToastContext value={show}>
      {children}
      {message && (
        <div className="gf-toast" aria-hidden="true" data-testid="toast">
          {message}
        </div>
      )}
    </ToastContext>
  );
}
